import { type ComponentType } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../auth";
import { Icons } from "./icons";
import { useGetSidebarStatsQuery } from "../store/stats";

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface NavItem {
  to: string;
  label: string;
  description: string;
  icon: ComponentType<{ size?: number; sw?: number }>;
  count?: string | null;
  danger?: boolean;
}

interface SidebarProps {
  /** Llamado cuando se selecciona un nav item (para cerrar el drawer en mobile). */
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps = {}) {
  const { user } = useAuth();
  // RTK Query: cache compartido entre navegaciones; ya no se re-llama en cada
  // re-mount del Sidebar. Pooling cada 30s para mantener los counters al día.
  const { data: counts } = useGetSidebarStatsQuery(undefined, { pollingInterval: 30_000 });

  const groups: NavGroup[] = [
    {
      label: "Observabilidad",
      items: [
        {
          to: "/traces",
          label: "Traces",
          description: "Registro de cada llamada externa, agrupado por correlation_id",
          icon: Icons.Activity,
          // Sin contador a propósito: la cantidad de traces crece sin tope (cada
          // llamada externa suma uno) y el número en el sidebar pierde valor.
          // El usuario ya tiene el histograma + búsqueda dentro de /traces.
          count: null,
        },
        {
          to: "/services",
          label: "Servicios",
          description: "Catálogo de sistemas observados y su estado en vivo",
          icon: Icons.Cube,
          count: counts ? formatCount(counts.services_total) : null,
        },
      ],
    },
    {
      label: "Operación",
      items: [
        {
          to: "/incidents",
          label: "Incidentes",
          description: "Issues operativos activos — afectan el estado de los servicios",
          icon: Icons.Flag,
          count: counts ? formatCount(counts.incidents_open) : null,
          danger: !!counts && counts.incidents_open > 0,
        },
        {
          to: "/subscribers",
          label: "Suscriptores",
          description: "A quién se le avisa cuando se abre un incident",
          icon: Icons.Users,
        },
      ],
    },
    {
      label: "Ajustes",
      items: [
        {
          to: "/docs",
          label: "Documentación",
          description: "Cómo funciona, cómo conectarse y endpoints disponibles",
          icon: Icons.Book,
        },
        {
          to: "/me",
          label: "Configuración",
          description: "Perfil, preferencias e integraciones",
          icon: Icons.Cog,
        },
      ],
    },
  ];

  if (user?.is_platform_admin) {
    groups.push({
      label: "Plataforma",
      items: [
        {
          to: "/organizations",
          label: "Organizaciones",
          description: "Tenants registrados",
          icon: Icons.Cube,
          count: null,
        },
      ],
    });
  }

  return (
    <nav className="sidebar">
      <div className="sidebar-head">
        <span className="sidebar-brand">Sovra Log</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {groups.map((group) => (
          <div key={group.label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div className="sidebar-section-label">{group.label}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.description}
                onClick={() => onNavigate?.()}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <item.icon size={15} sw={1.7} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.count != null && (
                  <span className={`count ${item.danger ? "danger" : ""}`}>{item.count}</span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-foot-line">
          <span className="status-dot ok" /> Ingesta · operativa
        </div>
      </div>
    </nav>
  );
}
